import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {
  installFromGlobalAppDefinition,
  queryGlobalAppDefinition,
  upgradeFromGlobalAppDefinition,
} from 'benchling-apps/BenchlingAppDAO';
import {openBenchlingAppPage} from 'benchling-apps/lib';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Title} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import LongTextValue from 'ui-library/LongTextValue';

interface Props {
  appDefinitionId: string;
}

export default function QueryingInstallBenchlingAppPage({appDefinitionId}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [version, setVersion] = useState('');
  const [isLatest, setIsLatest] = useState(false);
  const [appInstanceId, setAppInstanceId] = useState<string | null>(null);

  async function handleInstall(): Promise<void> {
    const resp = await installFromGlobalAppDefinition(appDefinitionId);
    notifications.success(`Successfully Installed App ${name}`);
    openBenchlingAppPage(resp.benchlingApp.id);
  }

  async function handleUpgrade(): Promise<void> {
    const resp = await upgradeFromGlobalAppDefinition(appDefinitionId);
    notifications.success(`Successfully Upgraded App ${name}`);
    openBenchlingAppPage(resp.benchlingApp.id);
  }

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const payload = await queryGlobalAppDefinition(appDefinitionId);
      const appDefinition = payload.benchlingAppDefinition;
      const appInstance = payload.benchlingAppInstance;

      setName(appDefinition.name);
      setDescription(appDefinition.description);
      setVersion(appDefinition.version);
      setIsLatest(payload.isLatest);
      setIsLoading(false);
      setAppInstanceId(appInstance ? appInstance.id : null);
    };

    loadData();
  }, [appDefinitionId]);

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <InstallBenchlingAppPage
      name={name}
      description={description}
      version={version}
      isInstalled={appInstanceId != null}
      isLatest={isLatest}
      handleInstall={handleInstall}
      handleUpgrade={handleUpgrade}
      handleNavToWorkspace={() => {
        if (appInstanceId != null) {
          openBenchlingAppPage(appInstanceId);
        }
      }}
    />
  );
}

interface InstallBenchlingAppPageProps {
  name: string;
  description: string;
  version: string;
  isInstalled: boolean;
  isLatest: boolean;
  handleInstall: () => Promise<void>;
  handleUpgrade: () => Promise<void>;
  handleNavToWorkspace: () => void;
}
export function InstallBenchlingAppPage({
  name,
  version,
  description,
  isLatest,
  isInstalled,
  handleInstall,
  handleUpgrade,
  handleNavToWorkspace,
}: InstallBenchlingAppPageProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  return (
    <div className={css(styles.contentWrapper)} role="main">
      <div className={css(styles.darkGreyText)}>BENCHLING APP</div>
      <div className="u-lr-split u-small-spacing-top">
        <div>
          <div className={css(styles.bigAppNameText)}>{name}</div>
          <span className={css(styles.darkGreyText)}>Latest Version: {version}</span>
        </div>
        <div className="u-lr-split">
          <Tooltip text={isLatest ? 'Latest version already installed' : null} isDisabled={!isLatest}>
            <Button
              size={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyle.PRIMARY}
              onClick={async () => {
                setIsSubmitting(true);

                if (isInstalled) {
                  await handleUpgrade();
                } else {
                  await handleInstall();
                }
                setIsSubmitting(false);
              }}
              text={isInstalled ? 'Upgrade' : 'Install'}
              disabled={isSubmitting || isLatest}
            />
          </Tooltip>
          {isInstalled && (
            <div className={css(styles.buttonMargin)}>
              <Button
                size={ButtonSize.MEDIUM}
                buttonStyle={ButtonStyle.SECONDARY}
                onClick={handleNavToWorkspace}
                text="View App in the workspace"
              />
            </div>
          )}
        </div>
      </div>
      <div className="u-spacing-top">
        <Title>Description</Title>
        <LongTextValue value={description} />
      </div>
    </div>
  );
}
const styles = StyleSheet.create({
  darkGreyText: {
    color: Colors.GRAY_DARKER,
  },
  bigAppNameText: {
    fontSize: '26px',
  },
  contentWrapper: {
    margin: '40px auto',
    width: '90%',
  },
  buttonMargin: {
    marginLeft: '10px',
  },
});
